import {
  Business as BusinessIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAuthInfoQuery,
} from "../../store/services/authService";
import {
  removeAuth,
  selectCompany,
  selectCurrentToken,
  selectExpires,
  selectUserNo,
  setAuth,
} from "../../store/slices/authSlice";
import { ExternalUserAuth } from "../../store/types/auth.types";

const SettingsPage = () => {
  const dispatch = useDispatch();
  const [showTokens, setShowTokens] = useState(false);

  // Redux selectors
  const currentToken = useSelector(selectCurrentToken);
  const company = useSelector(selectCompany);
  const expires = useSelector(selectExpires);
  const userNo = useSelector(selectUserNo);

  // RTK Query hooks
  const { data: authInfo, isLoading: authInfoLoading } = useGetAuthInfoQuery();

  const handleLogout = () => {
    dispatch(removeAuth());
    // Navigera till login-sidan eller reload
    window.location.href = "/login";
  };

  const handleCompanySwitch = async (companyAuth: ExternalUserAuth) => {
    // Implementera företagsbyte logik
    if (!companyAuth || !currentToken || !expires || !userNo) {
      console.error("Invalid companyAuth or currentToken");
      return;
    }
    try {
      dispatch(
        setAuth({
          token: companyAuth.Token,
          expires: expires,
          company:
            companyAuth.CompanyShortName === "Dalakraft" ? "Dalakraft" : "Enkla",
          userNo: userNo,
        })
      );
      window.location.reload(); // Ladda om sidan för att uppdatera kontexten
      console.log("Switching to company:", companyAuth);
    } catch (error) {
      console.error("Failed to switch company:", error);
    }
  };

  const getTokenStatus = (expires: string | null) => {
    if (!expires) return { color: "error", text: "Okänd" };

    const expiryDate = new Date(expires);
    const now = new Date();
    const hoursUntilExpiry =
      (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursUntilExpiry < 0) {
      return { color: "error", text: "Utgången" };
    } else if (hoursUntilExpiry < 24) {
      return {
        color: "warning",
        text: `${Math.floor(hoursUntilExpiry)}h kvar`,
      };
    } else {
      const daysUntilExpiry = Math.floor(hoursUntilExpiry / 24);
      return { color: "success", text: `${daysUntilExpiry} dagar kvar` };
    }
  };

  const tokenStatus = getTokenStatus(expires);

  if (authInfoLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Laddar inställningar...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Inställningar
          </Typography>
        </Stack>
        <Typography variant="subtitle1" color="text.secondary">
          Hantera dina autentiseringsinställningar och API-tokens
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Aktuell Session */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 3 }}
              >
                <SecurityIcon color="primary" />
                <Typography variant="h6" fontWeight="medium">
                  Aktuell Session
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Företag
                  </Typography>
                  <Chip
                    label={company}
                    color="primary"
                    variant="outlined"
                    icon={<BusinessIcon />}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Användar-ID
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body1">{userNo}</Typography>
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Token Status
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={tokenStatus.text}
                      size="small"
                      icon={<ScheduleIcon />}
                    />
                    {tokenStatus.color === "warning" && (
                      <Tooltip title="Token går ut snart">
                        <WarningIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    API Token
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TextField
                      value={showTokens ? currentToken : "••••••••••••••••"}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        style: {
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setShowTokens(!showTokens)}
                    >
                      {showTokens ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  size="small"
                  onClick={handleLogout}
                >
                  Logga ut
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Företagsväxling */}
        {authInfo?.CompanyAuths && authInfo.CompanyAuths.length > 1 && (
          <Grid item xs={12} lg={6}>
            <Card elevation={2}>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 3 }}
                >
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" fontWeight="medium">
                    Företagsväxling
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Du har åtkomst till flera företag. Välj vilket du vill arbeta
                  med.
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Välj företag</InputLabel>
                  <Select
                    value={company}
                    label="Välj företag"
                    onChange={(e) => {
                      const selectedAuth = authInfo.CompanyAuths.find(
                        (auth) => auth.CompanyShortName === e.target.value
                      );
                      if (selectedAuth) {
                        handleCompanySwitch(selectedAuth);
                      }
                    }}
                  >
                    {authInfo.CompanyAuths.map((auth) => (
                      <MenuItem
                        key={auth.CompanyNo}
                        value={auth.CompanyShortName}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {auth.CompanyShortName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {auth.CompanyName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {auth.CompanyShortName}
                            </Typography>
                          </Box>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Felsökning */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <WarningIcon color="warning" />
                <Typography variant="h6" fontWeight="medium">
                  Felsökning
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rensa lokalt sparade paneler om något beter sig konstigt.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    localStorage.removeItem("openActivityPanels");
                    window.location.reload();
                  }}
                >
                  Rensa aktivitetspaneler
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    localStorage.removeItem("openWorkPanels");
                    window.location.reload();
                  }}
                >
                  Rensa ärendepaneler
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;
